import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { remPx } from '../../lib/rem';

/**
 * Sizes the placement works out in px, given in rem so they follow index.css's 80% scale (px at full
 * size in the comments). `place()` multiplies them by `remPx()`.
 */
const VIEWPORT_MARGIN_REM = 0.5; // 8px kept clear at the window's edges.
const GAP_REM = 0.375; // 6px between the icon and its tip.

/**
 * How long the tip stays after the pointer leaves the icon, or the tip: time to cross the gap between
 * the two without the tip closing under the pointer on its way.
 */
const HIDE_DELAY_MS = 150;

/**
 * An "i" button beside a field's label, whose tooltip says what the field is for.
 *
 * The app had no tooltip component (every other hint is a native `title`, which a keyboard never
 * shows), so this is the first. It follows WCAG 1.4.13, content on hover or focus:
 *  - it shows on pointer hover AND on keyboard focus of the button;
 *  - it stays while the pointer is over the button or the tip itself, so the pointer can move onto
 *    the text to read it ("hoverable"), and while the button has focus ("persistent");
 *  - Escape hides it without moving the pointer or the focus ("dismissible"). The key goes no
 *    further while the tip is open: the dialog around it does not close too. A new hover or focus
 *    shows it again.
 *
 * The tip is always in the page, hidden while closed, and the button names it with
 * `aria-describedby`: a screen reader reads it with the button however the button is reached.
 *
 * It is drawn in the closest dialog (else the page body) with `position: fixed`, placed against the
 * window: a tip inside the composer's scrolling modal is never clipped by it, and a 300px tip that
 * starts at a label does not run off a 390px phone. That is how Select draws its list, for the same
 * reasons (Select.tsx).
 *
 * Text only. A link inside it could not be reached by keyboard: the tip closes as focus leaves the
 * button.
 */
export function InfoTooltip({
  label,
  children,
}: {
  /** The button's accessible name: what the tooltip is about ("About scheduling categories"). */
  label: string;
  /** The tooltip's text. */
  children: ReactNode;
}) {
  const tipId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<number | undefined>(undefined);

  /** The pointer is over the button or the tip (or left one of them less than HIDE_DELAY_MS ago). */
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  /** Escape was pressed: hidden until the next hover of the button, or its next focus. */
  const [dismissed, setDismissed] = useState(false);
  const open = (hovered || focused) && !dismissed;

  const keepHover = () => {
    window.clearTimeout(hideTimer.current);
    setHovered(true);
  };
  const dropHoverSoon = () => {
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setHovered(false), HIDE_DELAY_MS);
  };
  useEffect(() => () => window.clearTimeout(hideTimer.current), []);

  // The closest dialog, else the body. Inside an aria-modal dialog, content outside it is what
  // assistive tech is told to ignore, so a tip in the body would describe nothing there.
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    setPortalTarget(buttonRef.current?.closest<HTMLElement>('[role="dialog"]') ?? document.body);
  }, []);

  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  /** Put the open tip above the button (below it when there is more room there), on screen. */
  const place = useCallback(() => {
    const button = buttonRef.current;
    const tip = tipRef.current;
    if (!button || !tip) return;
    const r = button.getBoundingClientRect();
    const rem = remPx();
    const margin = VIEWPORT_MARGIN_REM * rem;
    const gap = GAP_REM * rem;
    const width = tip.offsetWidth;
    const height = tip.offsetHeight;
    const roomAbove = r.top - gap - margin;
    const roomBelow = window.innerHeight - r.bottom - gap - margin;
    const above = height <= roomAbove || roomAbove >= roomBelow;
    setPos({
      top: Math.max(margin, above ? r.top - gap - height : r.bottom + gap),
      // From the button's left edge, moved left only as far as the window's right edge needs.
      left: Math.max(margin, Math.min(r.left, window.innerWidth - margin - width)),
    });
  }, []);

  // position:fixed is measured from the window, so any ancestor scrolling moves the button out from
  // under the tip: place it again on every scroll (capture: the composer scrolls inside its modal)
  // and resize. A layout effect, so the first placement lands before the tip is painted.
  useLayoutEffect(() => {
    if (!open) return;
    place();
    const again = () => place();
    window.addEventListener('scroll', again, true);
    window.addEventListener('resize', again);
    return () => {
      window.removeEventListener('scroll', again, true);
      window.removeEventListener('resize', again);
    };
  }, [open, place]);

  // Escape hides the tip and stops there. On WINDOW in the capture phase: the dialogs close on
  // Escape from `document` (Modal in the capture phase, the composer's modal in the bubble phase),
  // and window's capture runs before either, so stopping the key here keeps the dialog open.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setDismissed(true);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-describedby={tipId}
        onMouseEnter={() => {
          setDismissed(false);
          keepHover();
        }}
        onMouseLeave={dropHoverSoon}
        onFocus={() => {
          setDismissed(false);
          setFocused(true);
        }}
        onBlur={() => setFocused(false)}
        // -m-1 p-1: a 24px target around the 16px icon that takes no more room than the icon - except
        // on the left, toward the label it sits beside (ml-0.5): the focus ring is drawn 4px outside
        // the target, in px that do not follow the 80% scale, and would cover the label's last letter.
        className="focus-ring -m-1 ml-0.5 inline-flex shrink-0 rounded-full p-1 text-ink-muted hover:text-ink-strong"
      >
        <Info className="h-4 w-4" aria-hidden />
      </button>
      {portalTarget &&
        createPortal(
          <div
            ref={tipRef}
            id={tipId}
            role="tooltip"
            // No display class: one would beat the [hidden] rule and show a closed tip.
            hidden={!open}
            onMouseEnter={keepHover}
            onMouseLeave={dropHoverSoon}
            style={{ position: 'fixed', top: pos?.top, left: pos?.left }}
            className="z-50 w-[18.75rem] max-w-[calc(100vw-2rem)] rounded-md bg-chrome-bar px-3 py-2 text-left text-xs font-normal text-white shadow-panel"
          >
            {children}
          </div>,
          portalTarget,
        )}
    </>
  );
}
